import { useMutation, type UseMutationOptions } from "@tanstack/react-query"
import type { ErrorOption } from "react-hook-form"

import { useCustomerContext } from "@unlikelystudio/react-ecommerce-hooks"

import type { Nullish } from "~/@types/generics"
import { SHOPIFY_MULTIPASS_PARAMS } from "~/lib/api/constants"
import { requestNextApiEndpoint } from "~/lib/api/utils/request-endpoint"
import { CHECKOUT_URL } from "~/lib/shopify/constants"
import { useGetCart } from "~/hooks/useGetCart"
import useLang from "~/hooks/useLang"
import useOnMutationError from "~/hooks/useOnMutationError"

export interface getMultipassCheckoutUrlResponse {
  errors: { message: string }[]
  urlToRedirect: string
}

export function useRedirectMultipassCheckoutUrl(
  mutationOptions?: UseMutationOptions<getMultipassCheckoutUrlResponse | null, unknown, { url: Nullish<string> }>,
  setError?: (name?: string, error?: ErrorOption) => void
) {
  const onMutationError = useOnMutationError(setError)
  const lang = useLang()
  const { cartPayload } = useGetCart()
  const { customerAccessToken } = useCustomerContext()
  const checkoutWebUrl = cartPayload?.checkoutUrl ?? ""

  return useMutation<getMultipassCheckoutUrlResponse | null, unknown, { url: Nullish<string> }>(
    async ({ url }) => {
      if (!url || !customerAccessToken) {
        window.location.href = checkoutWebUrl
        return null
      }

      // In the context of multipass checkout, the URL can be provided in two ways:
      // 1. A complete URL (starting with https://)
      // 2. A relative path (e.g. /checkout/123)
      // This check ensures we build the complete URL in the second case
      if (!url.startsWith(`https://`)) {
        url = `https://${CHECKOUT_URL}${url}`
      }

      return await requestNextApiEndpoint(SHOPIFY_MULTIPASS_PARAMS, {
        accessToken: customerAccessToken,
        payload: {
          url,
          lang,
        },
      })
    },
    {
      ...mutationOptions,
      onSuccess: async (data, ...rest) => {
        //! standardize error response
        const error = data?.errors?.[0]
        const errorPayload = { payload: [data?.errors?.[0]] }

        if (data?.urlToRedirect) {
          const url = data?.urlToRedirect
          window.location.href = url
        }

        if (error) {
          return onMutationError(errorPayload, rest, mutationOptions?.onError)
        }
      },
      onError: (err, ...rest) => {
        onMutationError(err, rest, mutationOptions?.onError)
      },
    }
  )
}
